import maplibregl, { type Map as MapLibreMap, type Marker } from "maplibre-gl";
import type { QuestSpotMapPin } from "./quest-spot-map-layers.ts";

export {
  QUEST_SPOT_DOM_FIT_MAX_ZOOM,
  QUEST_SPOT_DOM_FIT_PADDING_PX,
  QUEST_SPOT_DOM_PHOTO_PX,
  QUEST_SPOT_DOM_SINGLE_ZOOM,
  questSpotDomMapFitPadding,
  questSpotDomMapFitPaddingWithTitles,
} from "../../shared/quest-spot-map-fit.ts";

export interface QuestSpotDomMarkerHandlers {
  onSelect?: (index: number) => void;
  onMove?: (index: number, lat: number, lng: number) => void;
}

export interface QuestSpotDomMarkerSyncOpts extends QuestSpotDomMarkerHandlers {
  selectedIndex?: number | null;
  draggable?: boolean;
}

/** HTML markers for spot quests (photo + title); avoids MapLibre canvas sprite thumbnails. */
export class QuestSpotDomMarkers {
  private markers: Marker[] = [];
  private entries = new Map<number, { marker: Marker; root: HTMLButtonElement; photo: HTMLElement }>();
  private syncKey = "";
  private moveRefreshMap: MapLibreMap | null = null;

  clear(): void {
    for (const m of this.markers) m.remove();
    this.markers = [];
    this.entries.clear();
    this.syncKey = "";
  }

  /** MapLibre anchor uses translate(-50%,-50%); a sized flex root breaks that for 2+ markers. */
  private createAnchorRoot(root: HTMLButtonElement): HTMLDivElement {
    const anchor = document.createElement("div");
    anchor.className = "quest-spot-dom-marker-anchor";
    anchor.append(root);
    return anchor;
  }

  private ensureMoveRefresh(map: MapLibreMap): void {
    if (this.moveRefreshMap === map) return;
    this.moveRefreshMap = map;
    const refresh = (): void => {
      for (const { marker } of this.entries.values()) {
        const ll = marker.getLngLat();
        marker.setLngLat(ll);
      }
    };
    map.on("moveend", refresh);
    map.on("resize", refresh);
  }

  sync(map: MapLibreMap, pins: readonly QuestSpotMapPin[], opts: QuestSpotDomMarkerSyncOpts): void {
    const spots = pins.filter(
      (p) => p.kind === "spot" && Number.isFinite(p.lng) && Number.isFinite(p.lat) && p.index != null,
    );
    const key = JSON.stringify(
      spots.map((p) => ({
        i: p.index,
        lng: p.lng,
        lat: p.lat,
        t: p.title ?? "",
        u: p.thumbnailUrl ?? "",
        drag: opts.draggable === true,
      })),
    );
    if (key === this.syncKey) {
      this.setSelectedIndex(opts.selectedIndex);
      return;
    }
    this.syncKey = key;
    this.clear();
    this.ensureMoveRefresh(map);
    for (const pin of spots) {
      const index = pin.index!;
      const root = document.createElement("button");
      root.type = "button";
      root.className = "quest-spot-dom-marker";
      const selected = opts.selectedIndex === index;
      if (selected) root.classList.add("is-selected");
      const titleText = (pin.title ?? "").trim() || "…";
      root.setAttribute("aria-label", titleText);
      const photoWrap = document.createElement("span");
      photoWrap.className = "quest-spot-dom-marker-photo";
      const url = pin.thumbnailUrl?.trim() ?? "";
      if (url) {
        const img = document.createElement("img");
        img.className = "quest-spot-dom-marker-img";
        img.src = url;
        img.alt = "";
        img.decoding = "async";
        img.addEventListener(
          "error",
          () => {
            img.replaceWith(defaultPinEl());
          },
          { once: true },
        );
        photoWrap.append(img);
      } else {
        photoWrap.append(defaultPinEl());
      }
      const label = document.createElement("span");
      label.className = "quest-spot-dom-marker-title";
      label.textContent = titleText;
      root.append(photoWrap, label);
      root.addEventListener("click", (e) => {
        e.stopPropagation();
        if (opts.onSelect) opts.onSelect(index);
      });
      root.addEventListener("mousedown", (e) => {
        e.stopPropagation();
      });
      root.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          opts.onSelect?.(index);
        }
      });
      const anchor = this.createAnchorRoot(root);
      const marker = new maplibregl.Marker({
        element: anchor,
        anchor: "center",
        draggable: opts.draggable === true,
      })
        .setLngLat([pin.lng, pin.lat])
        .addTo(map);
      if (selected) marker.getElement().style.zIndex = "2";
      if (opts.draggable && opts.onMove) {
        marker.on("dragend", () => {
          const { lat, lng } = marker.getLngLat();
          opts.onMove?.(index, lat, lng);
        });
      }
      this.markers.push(marker);
      this.entries.set(index, { marker, root, photo: photoWrap });
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        for (const { marker } of this.entries.values()) {
          const ll = marker.getLngLat();
          marker.setLngLat(ll);
        }
      });
    });
  }

  setSelectedIndex(index: number | null | undefined): void {
    for (const [i, { root, marker }] of this.entries) {
      const on = index != null && i === index;
      root.classList.toggle("is-selected", on);
      const el = marker.getElement();
      el.style.zIndex = on ? "2" : "";
    }
  }
}

function defaultPinEl(): HTMLElement {
  const pin = document.createElement("span");
  pin.className = "quest-spot-dom-marker-pin";
  pin.setAttribute("aria-hidden", "true");
  return pin;
}
